import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  STORY_AGGREGATION_MODE,
  buildClientStoryPerformanceQuery,
  buildStoryPerformanceFetchKey,
  formatStoryPerformanceDate,
  normalizeStoryPerformancePayload,
} from "../../lib/utils/storyPerformanceReport"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

function storyArgKey(arg = {}) {
  const { clientId, startDate, endDate } = arg
  return buildStoryPerformanceFetchKey(
    clientId,
    formatStoryPerformanceDate(startDate),
    formatStoryPerformanceDate(endDate),
  )
}

/**
 * GET /api/v1/report/clients/{clientId}/story/performance?aggregation_mode=paid_and_organic&startDate=&endDate=
 */
export const fetchClientStoryPerformance = createAsyncThunk(
  "clientStoryPerformance/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }

      const start = formatStoryPerformanceDate(startDate)
      const end = formatStoryPerformanceDate(endDate)
      if (!start || !end) {
        return rejectWithValue("startDate and endDate are required")
      }

      const query = buildClientStoryPerformanceQuery({
        startDate: start,
        endDate: end,
        aggregationMode: STORY_AGGREGATION_MODE,
      })
      const data = await get(`report/clients/${clientId}/story/performance?${query}`)

      return {
        stories: normalizeStoryPerformancePayload(data),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildStoryPerformanceFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch story performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      return !skipIfFetchKeyLoaded(getState().clientStoryPerformance, storyArgKey(arg))
    },
  },
)

const initialState = {
  stories: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const nextKey = storyArgKey(action.meta.arg || {})
  const keepStaleStories =
    nextKey === state.fetchedFetchKey && Array.isArray(state.stories) && state.stories.length > 0

  state.status = "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  if (!keepStaleStories) {
    state.fetchedFetchKey = null
    state.stories = []
  }
}

function isStaleFetch(action, state) {
  return storyArgKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")
}

const clientStoryPerformanceSlice = createSlice({
  name: "clientStoryPerformance",
  initialState,
  reducers: {
    clearClientStoryPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientStoryPerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientStoryPerformance.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.stories = action.payload?.stories ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientStoryPerformance.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch story performance"
        state.stories = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientStoryPerformance } = clientStoryPerformanceSlice.actions
export default clientStoryPerformanceSlice.reducer
